import { jsPDF } from 'jspdf';
import { formatDisplayDate } from './dashboardData';
import { formatPercent } from './flujoCalculations';
import { drawPdfReportHeader, loadPdfReportLogo } from './pdfReportHeader';

const dark = [21, 25, 29];
const muted = [104, 114, 125];
const grid = [224, 227, 231];
const pageSize = 24;

const series = [
  { field: 'industria', label: 'Industria', color: [161, 92, 0] },
  { field: 'podrido', label: 'Podrido', color: [217, 45, 32] },
  { field: 'mermas', label: 'Merma total', color: [255, 121, 0], strong: true },
];

const filenamePart = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-zA-Z0-9]+/g, '-')
  .replace(/^-|-$/g, '')
  .toLowerCase();

const splitDays = (days) => {
  const pages = [];

  for (let index = 0; index < days.length; index += pageSize) {
    pages.push(days.slice(index, index + pageSize));
  }

  return pages;
};

const getChartMaximum = (days) => {
  const maximum = Math.max(
    ...days.flatMap((day) => series.map(({ field }) => day.daily.percentages[field] || 0)),
    0
  );

  return Math.max(10, Math.ceil(maximum / 5) * 5);
};

const drawLegend = (doc, y) => {
  const itemWidth = 44;
  const totalWidth = itemWidth * series.length;
  let x = (doc.internal.pageSize.getWidth() - totalWidth) / 2;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);

  series.forEach((item) => {
    doc.setFillColor(...item.color);
    doc.circle(x, y - 1, 1.3, 'F');
    doc.setTextColor(...dark);
    doc.text(item.label, x + 3.5, y);
    x += itemWidth;
  });
};

const drawChart = (doc, days, maximum) => {
  const left = 22;
  const top = 51;
  const width = 257;
  const height = 96;
  const bottom = top + height;
  const levels = 5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...muted);

  for (let level = 0; level <= levels; level += 1) {
    const value = maximum - (maximum * level / levels);
    const y = top + (height * level / levels);

    doc.setDrawColor(...grid);
    doc.setLineWidth(0.2);
    doc.line(left, y, left + width, y);
    doc.text(`${formatPercent(value)} %`, left - 3, y + 1, { align: 'right' });
  }

  doc.setDrawColor(...dark);
  doc.setLineWidth(0.35);
  doc.line(left, top, left, bottom);
  doc.line(left, bottom, left + width, bottom);

  const pointX = (index) => days.length === 1
    ? left + width / 2
    : left + (width * index / (days.length - 1));
  const pointY = (value) => bottom - (Math.max(0, value) / maximum) * height;
  const labelEvery = Math.max(1, Math.ceil(days.length / 12));

  days.forEach((day, index) => {
    if (index % labelEvery !== 0 && index !== days.length - 1) return;

    doc.setTextColor(...muted);
    doc.setFontSize(7);
    doc.text(formatDisplayDate(day.fecha).slice(0, 5), pointX(index), bottom + 5, { align: 'center' });
  });

  series.forEach((item) => {
    doc.setDrawColor(...item.color);
    doc.setFillColor(...item.color);
    doc.setLineWidth(item.strong ? 0.9 : 0.55);

    days.forEach((day, index) => {
      const value = day.daily.percentages[item.field] || 0;
      const x = pointX(index);
      const y = pointY(value);

      if (index > 0) {
        const previousValue = days[index - 1].daily.percentages[item.field] || 0;
        doc.line(pointX(index - 1), pointY(previousValue), x, y);
      }

      doc.circle(x, y, item.strong ? 1.1 : 0.75, 'F');
    });
  });
};

const drawAccumulatedSummary = (doc, accumulated, y) => {
  const gap = 3;
  const boxWidth = 49;
  const startX = (doc.internal.pageSize.getWidth() - series.length * boxWidth - (series.length - 1) * gap) / 2;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...dark);
  doc.text('Resumen acumulado del periodo', startX, y - 5);

  series.forEach((item, index) => {
    const x = startX + index * (boxWidth + gap);
    doc.setFillColor(248, 249, 250);
    doc.setDrawColor(...grid);
    doc.roundedRect(x, y, boxWidth, 19, 1.5, 1.5, 'FD');
    doc.setFillColor(...item.color);
    doc.circle(x + 5, y + 6, 1.4, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...muted);
    doc.text(item.label, x + 9, y + 7);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...dark);
    doc.text(`${formatPercent(accumulated.percentages[item.field])} %`, x + 5, y + 15);
  });
};

export const downloadTotalLossesChartPdf = async ({ days, ...options }) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const logoDataUrl = await loadPdfReportLogo();
  const dayPages = splitDays(days);
  const maximum = getChartMaximum(days);
  const accumulated = days[days.length - 1].accumulated;

  dayPages.forEach((pageDays, pageIndex) => {
    if (pageIndex > 0) doc.addPage();

    drawPdfReportHeader(doc, {
      logoDataUrl,
      pageNumber: pageIndex + 1,
      rightDetail: `Almacenes: ${options.warehouseLabel}`,
      subtitle: `Periodo: ${formatDisplayDate(options.desde)} - ${formatDisplayDate(options.hasta)}`,
      title: 'GRAFICO DE MERMAS TOTALES',
      totalPages: dayPages.length,
    });

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...dark);
    doc.text(`Variedad: ${options.varietyLabel}`, 22, 36);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...muted);
    doc.text(
      `${formatDisplayDate(pageDays[0].fecha)} - ${formatDisplayDate(pageDays[pageDays.length - 1].fecha)}`,
      279,
      36,
      { align: 'right' }
    );

    drawLegend(doc, 44);
    drawChart(doc, pageDays, maximum);
    drawAccumulatedSummary(doc, accumulated, 171);
  });

  const filename = [
    'grafico-mermas-totales',
    options.varietyId ? filenamePart(options.varietyLabel) : null,
    options.desde,
    options.hasta,
  ].filter(Boolean).join('-');

  doc.save(`${filename}.pdf`);
};
